import api from './api'

const BASE = '/tenant-files'

export interface TenantFile {
  id: number
  originalName: string
  storedName: string
  disk: string
  path: string
  category: 'image' | 'excel' | 'document' | 'other'
  mimeType: string | null
  sizeBytes: number
  uploadedBy: number | null
  metadata: Record<string, any> | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  isImage: boolean
  isExcel: boolean
  isPdf: boolean
  humanSize: string
  extension: string
}

export interface StorageInfo {
  usedBytes: number
  quotaBytes: number
  usedMb: number
  quotaMb: number
  usagePercent: number
  breakdown: Record<string, { bytes: number; mb: number }>
}

export interface FileListParams {
  category?: string
  mimeType?: string
  search?: string
  dateFrom?: string
  dateTo?: string
  sortBy?: 'name' | 'created_at' | 'size_bytes'
  sortDir?: 'asc' | 'desc'
  page?: number
  perPage?: number
}

export interface PaginatedFiles {
  data: TenantFile[]
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

export const tenantFileService = {
  /**
   * B1.3 — Upload files
   */
  uploadFiles(formData: FormData, onUploadProgress?: (progress: any) => void): Promise<any> {
    return api.post(`${BASE}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 120000, // 2 minutes for uploads
    })
  },

  /**
   * List files with filters and pagination
   */
  listFiles(params: FileListParams = {}): Promise<any> {
    return api.get<PaginatedFiles>(BASE, { params })
  },

  /**
   * Get single file metadata
   */
  getFile(id: number): Promise<any> {
    return api.get<TenantFile>(`${BASE}/${id}`)
  },

  /**
   * Download file (returns blob)
   */
  downloadFile(id: number): Promise<any> {
    return api.get(`${BASE}/${id}/download`, {
      responseType: 'blob',
    })
  },

  /**
   * Preview file inline (returns blob)
   */
  previewFile(id: number): Promise<any> {
    return api.get(`${BASE}/${id}/preview`, {
      responseType: 'blob',
    })
  },

  /**
   * Delete a file
   */
  deleteFile(id: number): Promise<any> {
    return api.delete(`${BASE}/${id}`)
  },

  /**
   * Bulk delete files
   */
  bulkDelete(ids: number[]): Promise<any> {
    return api.post(`${BASE}/bulk-delete`, { ids })
  },

  /**
   * Get storage usage info
   */
  getStorageInfo(): Promise<any> {
    return api.get<StorageInfo>(`${BASE}/storage-info`)
  },

  /**
   * Restore a soft-deleted file
   */
  restoreFile(id: number): Promise<any> {
    return api.post(`${BASE}/${id}/restore`)
  },

  /**
   * Generate a signed URL for public access
   */
  getSignedUrl(id: number, expiresIn: number = 60): Promise<any> {
    return api.get(`${BASE}/signed-url/${id}`, {
      params: { expiresIn },
    })
  },
}

export default tenantFileService
